import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Input, Menu } from '@material-ui/core';
import {
  SearchOutlined,
  FilterList,
  FormatLineSpacing,
  LockOutlined,
  Lock,
  LockOpenOutlined,
  LockOpen,
} from '@material-ui/icons';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import Button from '../../../components/Button';
import { Category, filterParams } from '../../../redux/categories/types';
import SideFilters from '../SideFilters';
import { light } from '../../../styles/palette';
import { ORDER_TYPES, RESPONSE_STATUS } from '../../../helpers/consts';

import './styles.css';
import { captureEvent } from '../../../analytics';

type Props = {
  intl: {
    messages: [];
  };
};

type StateProps = {
  inputSearchValue: string,
  categoriesList: Array<Category>,
  filterParams: filterParams,
  filterModalIsOpen: boolean,
  selectedCategoriesIds: Array<number>,
  action: string,
};

type DispatchProps = {
  handleSearchRequest: Function,
  setInputSearchValue: Function,
  updateFilterParams: Function,
  changeFilterModalStatus: Function,
  updateCategories: Function,
  setSelectedCategoriesIds: Function,
  listsDataRequest: Function,
  triggerSnackBarError: Function,
};

const MenuItemStyle = withStyles({
  root: {
    color: '#6A6A6A',
    fontSize: 12,
  },
})(MenuItem);

const sortItems = [
  {
    label: 'categories.nameAscending',
    sort: ORDER_TYPES.ALPHABETICAL_ASCENDING,
  },
  {
    label: 'categories.nameDescending',
    sort: ORDER_TYPES.ALPHABETICAL_DESCENDING,
  },
];

const SearchBar: React.FC<Props & DispatchProps & StateProps> = ({
  intl,
  inputSearchValue,
  categoriesList,
  filterParams,
  filterModalIsOpen,
  setInputSearchValue,
  handleSearchRequest,
  updateFilterParams,
  changeFilterModalStatus,
  updateCategories,
  setSelectedCategoriesIds,
  selectedCategoriesIds,
  action,
  listsDataRequest,
  triggerSnackBarError,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const totalSelectedCategories = selectedCategoriesIds.length > 0 ? `(${selectedCategoriesIds.length})` : '';

  const sortType = filterParams.sort;

  const handleSort = type => {
    const newFilters = { ...filterParams, sort: type };
    updateFilterParams(newFilters);
    handleSearch(newFilters);
    handleCloseSort();
    captureEvent('sortCategories', { sort: type });
  };

  const handleOpenSort = event => {
    captureEvent('openSortCategories');
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSort = () => setAnchorEl(null);

  const handleSearch = params => handleSearchRequest(params, inputSearchValue);

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch(filterParams);
      captureEvent('searchCategories');
    }
  };

  const handleActivateInactivateCategories = () => {
    // eslint-disable-next-line object-shorthand
    captureEvent('openActivateCategories', { action: action });
    updateCategories();
  };

  const handleLoadFilter = () => {
    const hasData = categoriesList.length;
    if (!hasData) listsDataRequest({ categories: true });
    changeFilterModalStatus(true);
  };

  const renderSortMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseSort}
      PaperProps={{
        style: {
          padding: 0,
          transform: 'translateY(+25%)',
        },
      }}
    >
      {sortItems.map(item => (
        <MenuItemStyle
          style={{
            color: (item.sort === sortType ? light.primaryDark : light.gray.dark),
            backgroundColor: (item.sort === sortType ? light.primaryLight : light.white),
          }}
          key={item.sort}
          onClick={() => handleSort(item.sort)}
        >
          {intl.messages[item.label]}
        </MenuItemStyle>
      ))}
    </Menu>
  );

  const renderActivateInactivate = () => (
    <>
      {filterParams.active
        ? <>{selectedCategoriesIds.length > 0 ? <Lock /> : <LockOpenOutlined />}</>
        : <>{selectedCategoriesIds.length > 0 ? <LockOpen /> : <LockOutlined />}</> }
      <p>{`${totalSelectedCategories} ${action}`}</p>
    </>
  );

  return (
    <>
      <div className="search-container">
        <div className="search-bar">
          <Input
            className="search-input"
            autoFocus
            onKeyPress={handleKeyPress}
            onChange={e => setInputSearchValue(e.target.value)}
            value={inputSearchValue}
            disableUnderline
            placeholder={`${intl.messages['categories.search']}`}
          />
          <div>
            <SearchOutlined
              className="searchIcon"
              onClick={() => {
                handleSearch(filterParams);
                captureEvent('searchCategories');
              }}
            />
          </div>
        </div>
        <SideFilters
          isOpen={filterModalIsOpen}
          changeFilterModalStatus={changeFilterModalStatus}
          intl={intl}
          categoriesList={categoriesList}
          filterParams={filterParams}
          updateFilterParams={updateFilterParams}
          handleSearch={handleSearch}
          inputSearchValue={inputSearchValue}
          setSelectedCategoriesIds={setSelectedCategoriesIds}
        />
        <div className="separator" />
        <div className="button-container">
          <Button
            disableRipple
            fullWidth
            variant="text"
            onClick={() => {
              handleLoadFilter();
              captureEvent('openFilterCategories');
            }}
          >
            <FilterList />
            <p>{intl.messages['categories.filterSearch']}</p>
          </Button>
        </div>
        <div className="separator" />
        <div className="button-container">
          <Button
            fullWidth
            variant="text"
            onClick={handleOpenSort}
            disableRipple
          >
            <FormatLineSpacing />
            <p>{intl.messages['categories.sortList']}</p>
          </Button>
        </div>
        {renderSortMenu}
        <div className="separator" />
        <div className="button-container">
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              if (selectedCategoriesIds.length > 0) {
                handleActivateInactivateCategories();
              } else {
                triggerSnackBarError();
              }
            }}
            disableRipple
          >
            {renderActivateInactivate()}
          </Button>
        </div>
        {/* <div className="separator" />
        <div className="button-container">
          <Button
            fullWidth
            variant="text"
            onClick={() => {
              if (selectedReminders.length > 0) {
                deleteReminders();
                captureEvent('openDeleteReminders', { action: 'delete' });
              } else {
                triggerError(intl.messages['reminders.errors.unselected_reminders']);
                captureEvent('openDeleteReminders', {
                  action: 'delete',
                  error: intl.messages['reminders.errors.unselected_reminders'],
                });
              }
            }}
            disableRipple
          >
            {selectedReminders.length > 0 ? <Delete /> : <DeleteOutline />}
            <p>{`${totalSelectedReminders} ${intl.messages['utils.delete']}`}</p>
          </Button>
        </div> */}
      </div>
    </>
  );
};

export default injectIntl(SearchBar);
